import React from "react"
import {
  CalendarEventAvailabilitySlot,
  SubmitCalendarEventMutation,
  useCalendarEventQuery,
  useSubmitCalendarEventMutation,
} from "../../../../api/backend/backend-api"
import CustomAlert from "../../../../ui/atoms/Alert"
import Loader from "../../../../ui/atoms/Loader"
import { CalendarDate } from "../../../../ui/organisms/CustomCalendar/types"
import { useLabels } from "../../../contents/hooks"
import EventCalendarControl from "../components/EventCalendarControl"
import {
  calendarDateToString,
  formatTime,
  parseCalendarDate,
} from "../../utils/format"
import { trackReservations } from "../../../tracking/index"

export interface EventCalendarProps {
  calendarAlias: string
}

const EventCalendar = ({ calendarAlias }: EventCalendarProps) => {
  const { getLabel } = useLabels()
  const { data, loading, error } = useCalendarEventQuery({
    variables: {
      alias: calendarAlias,
    },
    fetchPolicy: "no-cache",
  })
  const [selectedDate, setSelectedDate] = React.useState<CalendarDate>()
  const [
    selectedSlot,
    setSelectedSlot,
  ] = React.useState<CalendarEventAvailabilitySlot>()
  const [slotConfirmed, setSlotConfirmed] = React.useState(false)
  const [
    submitData,
    setSubmitData,
  ] = React.useState<SubmitCalendarEventMutation>()

  const clearData = () => {
    setSelectedDate(undefined)
    setSelectedSlot(undefined)
    setSlotConfirmed(false)
    setSubmitData(undefined)
  }

  React.useEffect(() => {
    clearData()
  }, [calendarAlias])

  React.useEffect(() => {
    if (
      data?.calendarEvent?.definition.options?.preSelection?.preselectedDate
    ) {
      setSelectedDate(
        parseCalendarDate(
          data.calendarEvent.definition.options.preSelection.preselectedDate
        )
      )
    }
  }, [data?.calendarEvent?.definition.options?.preSelection?.preselectedDate])

  const handleDateChange = (value: CalendarDate) => {
    setSelectedSlot(undefined)
    setSelectedDate(value)
  }

  const handleSlotChange = (value: CalendarEventAvailabilitySlot) => {
    setSelectedSlot(value)
    setSlotConfirmed(false)
  }

  const handleSlotConfirm = () => setSlotConfirmed(true)

  const [
    submitEvent,
    { loading: submitLoading, error: submitError },
  ] = useSubmitCalendarEventMutation()

  const handleSubmit = async (formData: any) => {
    if (!data?.calendarEvent || !selectedDate || !selectedSlot) {
      console.error("Missing info")
      return
    }
    const submitData = await submitEvent({
      variables: {
        input: {
          calendarId: data.calendarEvent.definition.id as string,
          data: formData,
          date: {
            day: selectedDate.day,
            month: selectedDate.month,
            year: selectedDate.year,
          },
          from: {
            hour: selectedSlot.from.hour,
            minute: selectedSlot.from.minute,
          },
          to: {
            hour: selectedSlot.to.hour,
            minute: selectedSlot.to.minute,
          },
        },
      },
    })
    trackReservations({
      calendarId: data.calendarEvent.definition.id as string,
      date: calendarDateToString(selectedDate),
      name: formData.name,
      email: formData.email,
      from: formatTime(selectedSlot.from),
      to: formatTime(selectedSlot.to),
    })
    setSubmitData(submitData.data ?? undefined)
  }

  return (
    <div>
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {data?.calendarEvent && (
        <div className="my-16 sm:px-8">
          <EventCalendarControl
            calendar={data.calendarEvent}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            onSelectedDateChange={handleDateChange}
            onSelectedSlotChange={handleSlotChange}
            onSlotConfirm={handleSlotConfirm}
            slotConfirmed={slotConfirmed}
            onSubmit={handleSubmit}
            submitting={submitLoading}
            submitCompleted={!!submitData?.submitCalendarEvent?.success}
            submitError={
              submitError !== undefined ||
              submitData?.submitCalendarEvent?.success === false
            }
            submitErrorType={
              submitData?.submitCalendarEvent?.error ?? undefined
            }
            confirmedEvent={submitData?.submitCalendarEvent?.event ?? undefined}
          />
        </div>
      )}
      <CustomAlert show={!!error} severity="error">
        {getLabel("messages", "generic-error")}
      </CustomAlert>
    </div>
  )
}

export default EventCalendar
